import { useState } from 'react'
import { formatHuman } from '../data/workdate.js'
import './workday.css'
import './review.css'

import backChevron from '../assets/figma/workday/back-chevron.svg'
import bannerIcon from '../assets/figma/workday/banner-review.svg'
import recordedIcon from '../assets/figma/workday/evidence-recorded.svg'
import reportedIcon from '../assets/figma/workday/evidence-reported.svg'
import lockIcon from '../assets/figma/workday/governance-lock.svg'

/*
 * State E — Workday Review Required, implemented from the approved Figma
 * prototype frame 9:2. Structure, spacing, type and colour are the Figma
 * values — see review.css.
 *
 * The screen holds TWO accounts side by side and reconciles neither:
 *   WHAT FLOW RECORDED       — the events, including the one that is missing
 *   WHAT THE EMPLOYEE REPORTED — their words, with the time they said them
 *
 * 9:52 is the behaviour, not a caption: both records are preserved separately,
 * no earlier check-out is manufactured, and payroll validation stays blocked
 * until an authorized reviewer resolves it. Submitting from here writes the
 * employee's account and NOTHING else — it never sets a checkedOutAt.
 */
export default function WorkdayReview({ adapter, session, day, onHome, onDone }) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [report, setReport] = useState(day?.employeeReport ?? null)

  const employeeId = session?.employee?.id ?? null

  async function submit() {
    const words = text.trim()
    if (!words || busy || !employeeId) return
    setBusy(true)
    const now = new Date().toISOString()
    try {
      const res = await adapter.submitWorkdayReview(employeeId, day.date, words, now)
      if (res.success) {
        setReport({ text: words, submittedAt: res.submittedAt })
        onDone?.()
      }
    } catch { /* stays on screen; the words are still in the field */ }
    setBusy(false)
  }

  return (
    <div className="wd" data-figma="9:2">
      <div className="wd-header">
        <button type="button" className="wd-back" onClick={onHome}>
          <img className="wd-back__icon" src={backChevron} alt="" />
          Home
        </button>
        <p className="wd-title">WORKDAY</p>
        <div className="wd-header__spacer" />
      </div>

      <div className="wd-body wd-body--rv">
        <div className="rv-banner" data-figma="9:21">
          <img className="rv-banner__icon" src={bannerIcon} alt="" />
          <p className="rv-banner__text">Workday Review Required</p>
        </div>

        <div className="rv-gap-20" />
        {/*
          9:28 reads TODAY. This screen is reached for a day that has already
          ended, so the date it actually concerns is named instead — showing
          "TODAY" above a different day's events would misstate the record,
          which is the one thing this screen exists to prevent.
        */}
        <p className="rv-today" data-figma="9:28">{dayLabel(day?.date)}</p>
        <div className="rv-gap-12" />

        <div className="rv-evidence" data-figma="9:30">
          <div className="rv-evidence__head">
            <img className="rv-evidence__icon" src={recordedIcon} alt="" />
            <p className="rv-evidence__label">WHAT FLOW RECORDED</p>
          </div>
          <p className="rv-line">Check In &mdash; {timeLabel(day?.checkedInAt)}</p>
          {/* The absence IS the record. Never filled in with a guess. */}
          <p className="rv-line rv-line--absent">Check Out &mdash; no event recorded</p>
        </div>

        <div className="rv-gap-12" />

        <div className="rv-evidence" data-figma="9:39">
          <div className="rv-evidence__head">
            <img className="rv-evidence__icon" src={reportedIcon} alt="" />
            <p className="rv-evidence__label">WHAT THE EMPLOYEE REPORTED</p>
          </div>
          {report ? (
            <>
              <p className="rv-line">&ldquo;{report.text}&rdquo;</p>
              <p className="rv-submitted">Submitted {timeLabel(report.submittedAt)}</p>
            </>
          ) : (
            /* NEW / NOT YET FIGMA-APPROVED — 9:2 draws only the submitted
               state; this is how the words get there. */
            <textarea
              className="rv-input"
              rows={2}
              placeholder="What time did you stop, and what happened?"
              aria-label="What time did you stop, and what happened?"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          )}
        </div>

        <div className="rv-gap-14" />

        <div className="rv-caveat" data-figma="9:48">
          <img className="rv-caveat__icon" src={lockIcon} alt="" />
          <p className="rv-caveat__text">
            Both records are preserved separately &mdash; no earlier check-out is
            manufactured. Automatic payroll validation stays blocked until an
            authorized reviewer resolves this.
          </p>
        </div>

        <div className="wd-grow" />

        {report ? (
          <button type="button" className="rv-cta" onClick={onHome}>Done</button>
        ) : (
          <button type="button" className="rv-cta" data-figma="9:54"
                  disabled={busy || !text.trim()} onClick={submit}>
            {busy ? 'Submitting…' : 'Submit for Review'}
          </button>
        )}
      </div>
    </div>
  )
}

function dayLabel(iso) {
  if (!iso) return 'TODAY'
  return String(formatHuman(iso)).toUpperCase()
}

function timeLabel(iso) {
  if (!iso) return 'no event recorded'
  try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) }
  catch { return 'no event recorded' }
}
